import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Users } from "lucide-react";
import FriendAvatar from "@/components/FriendAvatar";
import { normalizeFriendName, type Friend, type SplitGroup } from "@boo/shared";
import { useFriendsStore } from "../store/useFriendsStore";
import { INPUT_CLASS } from "../types/friends.types";

interface FriendPickerProps {
  /** Friends already taken (on the bill / in the group); hidden from the list. */
  excludeIds?: string[];
  /** When set, split groups are offered too. */
  groups?: SplitGroup[];
  placeholder?: string;
  onPickFriend: (friend: Friend) => void | Promise<void>;
  onPickGroup?: (group: SplitGroup) => void | Promise<void>;
}

export default function FriendPicker({
  excludeIds = [],
  groups,
  placeholder = "Add a friend",
  onPickFriend,
  onPickGroup,
}: FriendPickerProps) {
  const { friends, loadFriends, addFriend } = useFriendsStore();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void loadFriends();
  }, [loadFriends]);

  useEffect(() => {
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  const key = normalizeFriendName(query);
  const matchingFriends = useMemo(
    () =>
      friends.filter(
        (f) =>
          !excludeIds.includes(f.id) &&
          normalizeFriendName(f.name).includes(key),
      ),
    [friends, excludeIds, key],
  );
  const matchingGroups = useMemo(
    () =>
      (groups ?? []).filter(
        (g) => g.members.length > 0 && g.name.toLowerCase().includes(key),
      ),
    [groups, key],
  );
  const exact = friends.find((f) => normalizeFriendName(f.name) === key);
  const canCreate = key.length > 0 && !exact;

  async function run(action: () => void | Promise<void>) {
    setBusy(true);
    try {
      await action();
      setQuery("");
      setOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Something went wrong",
      );
    } finally {
      setBusy(false);
    }
  }

  const rowClass =
    "flex w-full min-h-10 items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-accent active:bg-accent disabled:opacity-40";

  return (
    <div ref={rootRef} className="relative min-w-0 flex-1 basis-36">
      <input
        aria-label={placeholder}
        value={query}
        placeholder={placeholder}
        disabled={busy}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          if (e.key === "Enter") {
            e.preventDefault();
            if (exact && !excludeIds.includes(exact.id)) {
              void run(() => onPickFriend(exact));
            } else if (canCreate) {
              void run(async () => onPickFriend(await addFriend(query.trim())));
            }
          }
        }}
        className={`${INPUT_CLASS} w-full text-base sm:text-sm`}
      />
      {open && (
        <ul className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-card py-1 shadow-lg">
          {onPickGroup &&
            matchingGroups.map((g) => (
              <li key={g.id}>
                <button
                  type="button"
                  disabled={busy}
                  className={rowClass}
                  onClick={() => void run(() => onPickGroup(g))}
                >
                  <Users className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate">{g.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {g.members.length}
                  </span>
                </button>
              </li>
            ))}
          {matchingFriends.map((f) => (
            <li key={f.id}>
              <button
                type="button"
                disabled={busy}
                className={rowClass}
                onClick={() => void run(() => onPickFriend(f))}
              >
                <FriendAvatar
                  name={f.name}
                  avatar={f.avatar}
                  friendId={f.id}
                  size={24}
                />
                <span className="min-w-0 flex-1 truncate">{f.name}</span>
              </button>
            </li>
          ))}
          {canCreate && (
            <li>
              <button
                type="button"
                disabled={busy}
                className={`${rowClass} text-primary`}
                onClick={() =>
                  void run(async () =>
                    onPickFriend(await addFriend(query.trim())),
                  )
                }
              >
                <Plus className="size-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">
                  Create friend &lsquo;{query.trim()}&rsquo;
                </span>
              </button>
            </li>
          )}
          {!canCreate &&
            matchingFriends.length === 0 &&
            matchingGroups.length === 0 && (
              <li className="px-3 py-2 text-sm text-muted-foreground">
                No one left to add
              </li>
            )}
        </ul>
      )}
    </div>
  );
}
